import { TableAlign } from '../../components/Table'
import { SortableColumn } from '../../hooks/use-sort-data'

export enum OptionsQuery {
  Ticker = 't',
  Expiry = 'e',
  Strike = 's',
  Order = 'o',
  View = 'ov',
  Type = 'ty',
}

export enum OptionsSubpage {
  Prices = 'oc',
  Volatility = 'ocv',
}

export enum OptionsView {
  ChainByDate = 'chain_date',
  ChainByStrike = 'chain_strike',
  ListByDate = 'list_date',
  ListByStrike = 'list_strike',
  Plot = 'plot',
}

export enum OptionType {
  Call = 'call',
  Put = 'put',
}

export interface Option {
  type: OptionType
  ticker: string
  exDate: number
  strike: number
  openInterest: number
  averageVolume: number
  bidPrice: number
  askPrice: number
  lastClose: number
  lastChange: number
  lastSize: number
  lastVolume: number
  lastTime: string
  iv: number | null
  delta: number | null
  gamma: number | null
  theta: number | null
  vega: number | null
  rho: number | null
}

export interface OptionRow {
  strike: number
  exDate: number
  call: Option | null
  put: Option | null
}

export interface LoaderBase {
  ticker: string | null
  options: Option[]
  lastClose: number | null
  lastTime: number | null
  hasData?: boolean
}

export interface OptionsByDateLoader extends LoaderBase {
  subpage: OptionsSubpage
  view: OptionsView.ChainByDate | OptionsView.ListByDate
  expiries: string[]
  currentExpiry: string
}

export interface OptionsByStrikeLoader extends LoaderBase {
  subpage: OptionsSubpage
  view: OptionsView.ChainByStrike | OptionsView.ListByStrike
  strikes: number[]
  currentStrike: number
}

export interface PlotLoader {
  subpage: OptionsSubpage
  view: OptionsView.Plot
  optionable: boolean
  options: Option[]
}

export type OptionsLoaderData = OptionsByDateLoader | OptionsByStrikeLoader | PlotLoader

export enum ColumnId {
  ContractName = 'contractName',
  LastClose = 'lastClose',
  ChangeUsd = 'lastChange',
  ChangePct = 'changePct',
  BidPrice = 'bidPrice',
  AskPrice = 'askPrice',
  Volume = 'lastVolume',
  OpenInterest = 'openInterest',
  StrikePrice = 'strike',
  ExpirationDate = 'exDate',
  LastTrade = 'lastTime',
  IV = 'iv',
  Delta = 'delta',
  Gamma = 'gamma',
  Theta = 'theta',
  Vega = 'vega',
  Rho = 'rho',
}

export enum RowKind {
  OptionRow, // Base row which includes both call and put. Used to render strike price
  Call,
  Put,
}

interface OptionRowKind {
  ticker?: string
  lastClose?: number | null
  kind: RowKind.OptionRow
  row: OptionRow
}

export interface CallKind {
  ticker?: string
  lastClose?: number | null
  kind: RowKind.Call
  row: Option
}

interface PutKind {
  ticker?: string
  lastClose?: number | null
  kind: RowKind.Put
  row: Option
}

export type ColumnRendererProps = CallKind | PutKind | OptionRowKind

export interface TableColumnDefinition extends SortableColumn<ColumnId, ColumnRendererProps> {
  name: string
  align?: TableAlign
  className?: string
  renderColumn: (props: ColumnRendererProps) => React.ReactNode
}

export type MappedSubpageOrders = { [key in OptionsSubpage]?: string }
